
import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Settlement } from "@/types/payment";
import { Expense } from "@/types/expense";
import { 
  calculateMemberBalances,
  categorizeMembersByBalance,
  generateOptimizedSettlements 
} from "@/utils/settlementCalculators";
import { 
  SettlementCalculationCache,
  canUseCache, 
  createCacheEntry 
} from "@/utils/settlementCache";

export const useGroupSettlements = (
  groupId: string | undefined,
  expenses: Expense[],
  settlementCounter: number
) => {
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [paidSettlements, setPaidSettlements] = useState<Settlement[]>([]);
  const isMounted = useRef(true);
  const lastCalculation = useRef<SettlementCalculationCache | null>(null);

  // Memoized settlement calculation to avoid redundant heavy calculations
  const calculateSettlements = useCallback((paidSettlements: Settlement[]): Settlement[] => {
    console.log('Calculating settlements from expenses considering paid settlements');
    
    // Check if we can use the memoized result (same inputs = same output)
    if (canUseCache(lastCalculation.current, expenses, paidSettlements)) {
      console.log('Using memoized settlement calculation result');
      return lastCalculation.current.result;
    }
    
    if (!expenses || expenses.length === 0) {
      console.log('No expenses to calculate settlements from');
      return [];
    }
    
    // Calculate member balances based on expenses and paid settlements
    const balances = calculateMemberBalances(expenses, paidSettlements);
    
    // Split members into debtors and creditors
    const { debtors, creditors } = categorizeMembersByBalance(balances);
    
    // Generate optimized settlement transactions
    const newSettlements = generateOptimizedSettlements(debtors, creditors);
    
    // Store the calculation result for future reference
    lastCalculation.current = createCacheEntry(expenses, paidSettlements, newSettlements);
    
    console.log('Calculated settlements:', newSettlements);
    return newSettlements;
  }, [expenses]);

  // Fetch settlements from database or calculate them
  const fetchSettlements = useCallback(async () => {
    if (!groupId || !isMounted.current) return;
    
    console.log('Fetching settlements...');
    try {
      // First, fetch all saved settlements from database including paid ones
      const { data: savedSettlements, error: settlementsError } = await supabase
        .from('settlements')
        .select('*')
        .eq('group_id', groupId);
      
      if (settlementsError) {
        console.error('Error fetching settlements:', settlementsError);
        return;
      }
      
      if (!savedSettlements) {
        console.log('No settlements data returned');
        return;
      }
      
      // Convert saved settlements to our Settlement type
      const paidDbSettlements = savedSettlements
        .filter(s => s.is_paid)
        .map(s => ({
          from: s.from_user || '',
          to: s.to_user || '',
          amount: Math.round(Number(s.amount) || 0), // Round to whole number for JPY
          currency: s.currency || 'JPY',
          isPaid: true,
          paidAt: s.paid_at || undefined
        }));
      
      // Set paid settlements
      if (isMounted.current) {
        setPaidSettlements(paidDbSettlements);
        
        // Calculate new settlements considering the paid ones
        const calculatedSettlements = calculateSettlements(paidDbSettlements);
        
        // Combine paid and calculated settlements
        const allSettlements = [
          ...paidDbSettlements,
          ...calculatedSettlements
        ];
        
        // Set all settlements
        setSettlements(allSettlements);
      }
    } catch (error) {
      console.error("Error in fetchSettlements:", error);
    }
  }, [groupId, calculateSettlements]);

  // Update settlements when expenses change or settlement counter changes
  useEffect(() => {
    isMounted.current = true;
    
    if (expenses.length > 0) {
      fetchSettlements();
    } else {
      // If no expenses, no settlements
      setSettlements([]);
      setPaidSettlements([]);
    }
    
    return () => {
      isMounted.current = false;
    };
  }, [expenses, settlementCounter, groupId, fetchSettlements]);

  return {
    settlements,
    paidSettlements,
    fetchSettlements
  };
};
